import { useEffect, useState } from 'react';
import type { DealCost, DealDetail, DealMilestone } from '@forecastify/shared';
import { Button, Dialog, Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';
import { amountText, parseAmount, useActiveCurrencies } from './plan-kit';
import type { DealRecord } from './use-deal-record';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D6 · the Terv tab's two small dialogs.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A milestone is a name and a date; an external cost a name, an amount and a date. Both post
 * through the record, so the plan's totals move with the answer.
 */

function DialogFooter({
  error,
  busy,
  onCancel,
  onSave,
}: {
  readonly error: string | null;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}) {
  const t = useT();
  return (
    <>
      {error === null ? null : (
        <div className="text-destructive text-[12px]" role="alert">
          {error}
        </div>
      )}
      <div className="mt-1 flex items-center justify-end gap-2">
        <Button variant="outline" size="lg" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button size="lg" disabled={busy} onClick={onSave}>
          {t('common.save')}
        </Button>
      </div>
    </>
  );
}

export function MilestoneDialog({
  open,
  milestone,
  record,
  onClose,
}: {
  readonly open: boolean;
  /** null: a new milestone. */
  readonly milestone: DealMilestone | null;
  readonly record: DealRecord;
  readonly onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(milestone?.name ?? '');
    setDueOn(milestone?.dueOn ?? '');
    setError(null);
  }, [open, milestone]);

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(t('dealPlan.milestones.errorName'));
      return;
    }
    setBusy(true);
    const refusal = await record.write(
      milestone === null ? '/milestones' : `/milestones/${milestone.id}`,
      milestone === null ? 'POST' : 'PUT',
      {
        name: name.trim(),
        dueOn: dueOn === '' ? null : dueOn,
        ...(milestone === null ? {} : { version: milestone.version }),
      },
    );
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={milestone === null ? t('dealPlan.milestones.new') : t('dealPlan.milestones.edit')}
      closeLabel={t('common.close')}
      width={440}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealPlan.milestones.column.name')} required>
          <TextInput
            autoFocus
            value={name}
            placeholder={t('dealPlan.milestones.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        <Field label={t('dealPlan.milestones.column.due')}>
          <DateField
            value={dueOn === '' ? null : dueOn}
            onChange={setDueOn}
            label={t('dealPlan.milestones.column.due')}
          />
        </Field>
        <DialogFooter error={error} busy={busy} onCancel={onClose} onSave={() => void submit()} />
      </div>
    </Dialog>
  );
}

export function CostDialog({
  open,
  deal,
  cost,
  record,
  defaultCurrency,
  onClose,
}: {
  readonly open: boolean;
  readonly deal: DealDetail;
  /** null: a new cost. */
  readonly cost: DealCost | null;
  readonly record: DealRecord;
  readonly defaultCurrency: string;
  readonly onClose: () => void;
}) {
  const t = useT();
  const currencies = useActiveCurrencies([
    cost?.currencyCode ?? '',
    defaultCurrency,
    deal.currencyCode,
  ]);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [incurredOn, setIncurredOn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(cost?.name ?? '');
    setAmount(amountText(cost?.amount ?? null));
    setCurrency(cost?.currencyCode ?? defaultCurrency);
    setIncurredOn(cost?.incurredOn ?? '');
    setError(null);
  }, [open, cost, defaultCurrency]);

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(t('dealPlan.costs.errorName'));
      return;
    }
    const value = parseAmount(amount);
    if (value === null) {
      setError(t('deals.error.value'));
      return;
    }
    setBusy(true);
    const refusal = await record.write(
      cost === null ? '/costs' : `/costs/${cost.id}`,
      cost === null ? 'POST' : 'PUT',
      {
        name: name.trim(),
        amount: value,
        currencyCode: currency,
        incurredOn: incurredOn === '' ? null : incurredOn,
        ...(cost === null ? {} : { version: cost.version }),
      },
    );
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={cost === null ? t('dealPlan.costs.new') : t('dealPlan.costs.edit')}
      description={t('dealPlan.costs.hint')}
      closeLabel={t('common.close')}
      width={480}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealPlan.costs.column.name')} required>
          <TextInput
            autoFocus
            value={name}
            placeholder={t('dealPlan.costs.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('dealPlan.costs.column.amount')}>
            <TextInput
              inputMode="decimal"
              className="tabular-nums"
              value={amount}
              placeholder="0"
              onChange={(event) => {
                setAmount(event.target.value);
                setError(null);
              }}
            />
          </Field>
          <Field label={t('dealPlan.field.currency')}>
            <Select
              value={currency}
              onChange={(event) => {
                setCurrency(event.target.value);
              }}
              options={currencies.map((code) => ({ value: code, label: code }))}
            />
          </Field>
          <Field label={t('dealPlan.costs.column.date')}>
            <DateField
              value={incurredOn === '' ? null : incurredOn}
              onChange={setIncurredOn}
              label={t('dealPlan.costs.column.date')}
            />
          </Field>
        </div>
        <DialogFooter error={error} busy={busy} onCancel={onClose} onSave={() => void submit()} />
      </div>
    </Dialog>
  );
}
